import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { TicketSearchByEnum } from '@/stores/useTicketingStore';

const MIN_CHARS = 3;

interface TicketSearchPillProps {
  searchText?: string;
  searchBy?: TicketSearchByEnum;
  onChange: (text: string, field: TicketSearchByEnum) => void;
  onClear: () => void;
}

/**
 * Smart search: determines search type AND normalizes the query text.
 * - "TKT-016510" or "TKT016510" → ticket ID lookup as-is
 * - Pure digits like "016510" or "0615" → ticket ID lookup with "TKT-" prepended
 * - Text with letters (BXO-xxx, etc.) → platform code search (broadest)
 */
function prepareSearch(text: string): { field: TicketSearchByEnum; query: string } {
  const trimmed = text.trim();
  const upper = trimmed.toUpperCase();

  // Explicit TKT prefix → ticket ID search
  if (upper.startsWith('TKT-') || upper.startsWith('TKT')) {
    return { field: TicketSearchByEnum.TICKET_ID, query: trimmed };
  }

  // Pure digits → likely a ticket number, prepend TKT- for ID lookup
  if (/^\d+$/.test(trimmed)) {
    return { field: TicketSearchByEnum.TICKET_ID, query: `TKT-${trimmed}` };
  }

  // Anything else → platform code search (broadest endpoint)
  return { field: TicketSearchByEnum.PLATFORM_CODE, query: trimmed };
}

const TicketSearchPill = ({ searchText, searchBy, onChange, onClear }: TicketSearchPillProps) => {
  const { t } = useTranslation();
  const [localText, setLocalText] = useState(searchText ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!searchText) setLocalText('');
  }, [searchText]);

  const executeSearch = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (trimmed.length >= MIN_CHARS) {
        const { field, query } = prepareSearch(trimmed);
        onChange(query, field);
      }
    },
    [onChange],
  );

  const handleClear = () => {
    setLocalText('');
    onClear();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch(localText);
    }
    if (e.key === 'Escape') {
      handleClear();
    }
  };

  const isActive = !!localText;
  const canSearch = localText.trim().length >= MIN_CHARS;
  const showHint = isActive && !canSearch;

  return (
    <div className='relative w-full max-w-[420px]'>
      <div
        className='flex items-center h-10 rounded-xl border border-solid bg-white transition-all duration-200'
        style={{
          borderColor: isFocused ? 'rgb(237,224,74)' : 'rgb(232,230,227)',
          boxShadow: isFocused ? '0 0 0 3px rgba(237,224,74,0.12)' : 'none',
        }}
      >
        {/* Search icon */}
        <span className='flex items-center justify-center w-10 shrink-0'>
          <Search
            size={16}
            style={{ color: isFocused || isActive ? 'rgb(57,57,60)' : 'rgb(156,163,175)' }}
            strokeWidth={2}
          />
        </span>

        {/* Input */}
        <input
          ref={inputRef}
          type='text'
          value={localText}
          onChange={(e) => setLocalText(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={String(
            t('search_tickets_placeholder', {
              defaultValue: 'Search by TKT#, platform code...',
            }),
          )}
          className='flex-1 min-w-0 h-full bg-transparent border-0 outline-none text-[13px] pe-1'
          style={{ color: 'rgb(57,57,60)' }}
        />

        {/* Right side: clear button OR enter hint */}
        <div className='flex items-center gap-1 shrink-0 me-2'>
          {isActive && (
            <button
              type='button'
              onClick={handleClear}
              className='flex items-center justify-center w-6 h-6 rounded-md shrink-0 bg-transparent border-0 cursor-pointer hover:bg-gray-100 transition-colors'
              aria-label='Clear search'
            >
              <X size={13} style={{ color: 'rgb(156,163,175)' }} strokeWidth={2} />
            </button>
          )}
          {canSearch && (
            <button
              type='button'
              onClick={() => executeSearch(localText)}
              className='flex items-center gap-1 h-6 px-1.5 rounded-md bg-sd-muted border-0 cursor-pointer hover:bg-gray-200 transition-all duration-150'
              title='Press Enter to search'
            >
              <CornerDownLeft size={12} style={{ color: 'rgb(115,123,140)' }} strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* Minimum chars hint */}
      {showHint && isFocused && (
        <p className='text-[11px] text-sd-muted-foreground mt-1 ms-2 m-0 animate-[fadeIn_150ms_ease-out]'>
          {String(t('min_chars_hint', { defaultValue: 'Type at least 3 characters, then press Enter' }))}
        </p>
      )}
    </div>
  );
};

export default TicketSearchPill;
